import PropertyContent from "@/components/property/PropertyContent";
import PropertyHeader from "@/components/property/PropertyHeader";
import PropertySearchBar from "@/components/property/PropertySearchBar"; // Import PropertySearchBar
import MinimizedFilterSidebar from "@/components/search/MinimizedFilterSidebar";
import SearchCallToActions from "@/components/search/SearchCallToActions";
import SearchFilters from "@/components/search/SearchFilters";
import BackToTopButton from "@/components/ui/back-to-top-button";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSearch } from "@/context/SearchContext";
import { useCitiesByDistrictId } from "@/hooks/useLocations"; // Changed from useLocations
import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const SearchResultsContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const {
    searchQuery,
    setSearchQuery,
    handleSearch,
    handleClearFilters,
    handleFilterChange,
    sortBy,
  } = useSearch();

  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // Prefetch property types and locations for reuse across components
  const { isLoading: loadingPropertyTypes } = usePropertyTypes();
  // Using useCitiesByDistrictId with undefined as districtId, as useLocations was not exported.
  // This component only uses the isLoading state.
  const { isLoading: loadingLocations } = useCitiesByDistrictId(undefined);

  // Get active category from URL params directly
  const listingType = searchParams.get("listing_type");
  const propertyType = searchParams.get("propertyType");

  // Get active category name from search params
  const activeCategoryName = useMemo(() => {
    if (!listingType) return "All Properties";

    // Map listing_type to display names
    switch (listingType) {
      case "sale":
        return "Properties for Sale";
      case "rent":
        return "Properties for Rent";
      case "lease":
        return "Properties for Lease";
      default:
        return "All Properties";
    }
  }, [listingType]);

  // Set the search query from URL params when component mounts
  useEffect(() => {
    const urlSearchQuery = searchParams.get("search_term");
    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }
  }, [searchParams, setSearchQuery]);

  // Create a filters object for the PropertyContent component
  const filters = useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      keywords: searchQuery || undefined,
      location: searchParams.get("location") || undefined,
      propertyType: propertyType || undefined,
      listing_type: listingType || undefined,
      minPrice: searchParams.get("minPrice")
        ? Number(searchParams.get("minPrice"))
        : undefined,
      maxPrice: searchParams.get("maxPrice")
        ? Number(searchParams.get("maxPrice"))
        : undefined,
      sort_by: searchParams.get("sort_by") || undefined,
      sort_order: searchParams.get("sort_order") || undefined,
    }),
    [searchParams, searchQuery, propertyType, listingType]
  );

  return (
    <div className="bg-realestate-gray pt-6 pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <PropertyHeader
          activeCategoryName={activeCategoryName}
          // searchQuery, setSearchQuery, handleSearch removed as they are for PropertySearchBar
          setShowFilterSheet={setShowFilterSheet}
        />

        {/* Dedicated Search Bar Section */}
        <div className="my-6">
          {" "}
          {/* Added margin for spacing */}
          <PropertySearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          <div className="hidden md:block w-full md:w-64 lg:w-72 flex-shrink-0">
            <SearchFilters />
          </div>

          <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
            <SheetContent
              side="left"
              className="w-[300px] sm:w-[400px] p-0 overflow-y-auto"
            >
              <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
                <div className="flex justify-between items-center">
                  <SheetTitle>Filters</SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon">
                      <X className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="p-4">
                <SearchFilters />
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-grow min-w-0">
            <PropertyContent onClearFilters={handleClearFilters} />
          </div>
        </div>

        <div className="mt-16">
          <SearchCallToActions />
        </div>
      </div>

      <MinimizedFilterSidebar />

      <BackToTopButton />
    </div>
  );
};

export default SearchResultsContent;
